import React, { MouseEventHandler, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaChartBar, FaChevronDown, FaChevronUp } from 'react-icons/fa';

import { COLOURS } from '@tq/ui-components/dist/pp2/theme';
import { spacing } from '@tq/ui-components/dist/components/mobile-options-accordion/styles/theme/spacing';
import { useThemedValueSwitch } from '@tq/ui-components';
import { useIsMobile } from '@tq/ui-components/dist/hooks';
import {
  Box,
  Button,
  Flex,
  Grid,
  Link,
  Typography,
  Heading,
} from '@tq/ui-components/dist/components';

import { useStatisticsReport } from '../../../../api';
import { useNavigate } from '../../../../hooks';
import { StatisticsChartDisplay } from './StatisticsChartDisplay';

export const StatisticsBar: React.FC = () => {
  const { tvs } = useThemedValueSwitch();
  const { t } = useTranslation('search_statistics');
  const { navigateToReports } = useNavigate();
  const isMobile = useIsMobile();
  const { data: statisticsResponse } = useStatisticsReport();

  const [showStatistics, setShowStatistics] = useState(false);
  const handleShowClick: MouseEventHandler<HTMLAnchorElement> = (e) => {
    e.preventDefault();
    setShowStatistics((prevShow) => !prevShow);
  };

  return isMobile ? (
    <Grid gridGap={8} mt={4}>
      <Button variant='secondary' onClick={navigateToReports}>
        {t('view_reports', 'View reports')}
      </Button>
      {statisticsResponse && (
        <StatisticsChartDisplay statisticsResponse={statisticsResponse} />
      )}
    </Grid>
  ) : (
    <Grid
      border={`1px solid ${COLOURS.gray[400]}`}
      borderRadius={5}
      role='region'
      aria-labelledby='latest-statistics'
    >
      <Flex justifyContent='space-between' px={5} py={7}>
        <Flex alignItems='center'>
          <Box
            mr={tvs({ tq: '0.5rem' }, 2)}
            color={tvs({ tq: 'blue.275' }, COLOURS.blue[400])}
            fontWeight='bold'
            fontSize='24px'
          >
            <FaChartBar />
          </Box>

          <Heading size='md' id='latest-statistics'>
            {t('latest_statistics', 'Latest Statistics')}
          </Heading>

          {showStatistics && (
            <Button
              ml={tvs({ tq: '3.625rem' }, 7)}
              px={6}
              height={spacing(6)}
              minWidth='unset !important'
              width={tvs({ tq: '8.125rem' })}
              variant='secondary'
              justifySelf='left'
              fontSize='sm'
              onClick={navigateToReports}
            >
              {t('view_reports', 'View reports')}
            </Button>
          )}
        </Flex>
        <Link onClick={handleShowClick} href='#'>
          <strong>
            <Flex gridGap={tvs({ tq: 3 }, 4)} alignItems='center'>
              <Typography fontSize='sm'>
                {showStatistics ? t('hide_statistics', 'Hide') : t('show_statistics', 'Show')}
              </Typography>
              <Typography>{showStatistics ? <FaChevronUp /> : <FaChevronDown />}</Typography>
            </Flex>
          </strong>
        </Link>
      </Flex>
      {showStatistics && (
        <Flex flexWrap='wrap' px={6} pb={showStatistics ? 6 : 0} gridGap={4}>
          {statisticsResponse && (
            <StatisticsChartDisplay
              width={'calc((100% - 32px) / 3)'}
              statisticsResponse={statisticsResponse}
            />
          )}
        </Flex>
      )}
    </Grid>
  );
};
